import { useCallback, useEffect, useRef, useState } from "react";
import { useAuthStore } from "store/auth";

// ============ Types ============
export interface OnlineUser {
  id: number;
  name: string;
  username: string;
}

export interface CommandData {
  awbNo?: string;
  pickupId?: string;
  message?: string;
}

export type CommandAction =
  | "TRACK_AWB"
  | "OPEN_PICKUP"
  | "TEXT_MESSAGE";

export interface IncomingCommand {
  type: "COMMAND";
  action: CommandAction;
  data: CommandData;
  from: OnlineUser;
  timestamp: number;
}

export interface SendCommandPayload {
  targetUserId: number;
  action: CommandAction;
  data: CommandData;
}

export type WSMessageType =
  | "CONNECTED"
  | "AUTH_ERROR"
  | "ONLINE_USERS"
  | "COMMAND"
  | "SENT"
  | "ERROR";

export interface WSMessage {
  type: WSMessageType;
  user?: OnlineUser;
  users?: OnlineUser[];
  message?: string;
  action?: CommandAction;
  targetUserId?: number;
  data?: CommandData;
  from?: OnlineUser;
  timestamp?: number;
}

export type ConnectionStatus = "disconnected" | "connecting" | "connected" | "auth_error";

// ============ Hook ============
interface UseCommandRelayOptions {
  onCommand?: (command: IncomingCommand) => void;
  onOnlineUsersChange?: (users: OnlineUser[]) => void;
  onError?: (message: string) => void;
  onSent?: (action: CommandAction, targetUserId: number) => void;
}

const BASE_URL = process.env.REACT_APP_BACKEND_URL || "https://api.airfield.express";
const WS_URL = BASE_URL.replace(/^http/, "ws") + "/ws";

export function useCommandRelay(options: UseCommandRelayOptions = {}) {
  const { onCommand, onOnlineUsersChange, onError, onSent } = options;

  const { token, user } = useAuthStore();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectDelayRef = useRef(1000);
  const MAX_RECONNECT_DELAY = 30000;

  const [status, setStatus] = useState<ConnectionStatus>("disconnected");
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [currentUser, setCurrentUser] = useState<OnlineUser | null>(null);

  // Store callbacks in refs to avoid re-connections on callback changes
  const callbacksRef = useRef({ onCommand, onOnlineUsersChange, onError, onSent });
  useEffect(() => {
    callbacksRef.current = { onCommand, onOnlineUsersChange, onError, onSent };
  }, [onCommand, onOnlineUsersChange, onError, onSent]);

  const handleMessage = useCallback((msg: WSMessage) => {
    switch (msg.type) {
      case "CONNECTED":
        setStatus("connected");
        if (msg.user) {
          setCurrentUser(msg.user);
        }
        console.log("[CommandRelay] Connected as:", msg.user?.username);
        break;

      case "ONLINE_USERS":
        if (msg.users) {
          setOnlineUsers(msg.users);
          callbacksRef.current.onOnlineUsersChange?.(msg.users);
        }
        break;

      case "COMMAND":
        console.log("[CommandRelay] WS COMMAND message received:", msg);
        if (msg.action && msg.data && msg.from && msg.timestamp !== undefined) {
          const command: IncomingCommand = {
            type: "COMMAND",
            action: msg.action,
            data: msg.data,
            from: msg.from,
            timestamp: msg.timestamp,
          };
          console.log("[CommandRelay] Calling onCommand callback with:", command);
          callbacksRef.current.onCommand?.(command);
        } else {
          console.warn("[CommandRelay] COMMAND message missing fields:", msg);
        }
        break;

      case "SENT":
        if (msg.action && msg.targetUserId !== undefined) {
          callbacksRef.current.onSent?.(msg.action, msg.targetUserId);
        }
        break;

      case "ERROR":
        console.error("[CommandRelay] Error:", msg.message);
        callbacksRef.current.onError?.(msg.message || "Unknown error");
        break;

      case "AUTH_ERROR":
        setStatus("auth_error");
        console.error("[CommandRelay] Auth failed:", msg.message);
        break;
    }
  }, []);

  const connect = useCallback(() => {
    if (!token) {
      setStatus("disconnected");
      return;
    }

    // Clean up existing connection
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setStatus("connecting");
    console.log("[CommandRelay] Attempting to connect to:", `${WS_URL}?token=${token?.slice(0, 10)}...`);
    const ws = new WebSocket(`${WS_URL}?token=${token}`);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log("[CommandRelay] WebSocket opened successfully to:", WS_URL);
      reconnectDelayRef.current = 1000; // Reset delay on success
    };

    ws.onmessage = (event) => {
      console.log("[CommandRelay] Raw WS message:", event.data);
      try {
        const msg: WSMessage = JSON.parse(event.data);
        console.log("[CommandRelay] Parsed WS message:", msg.type);
        handleMessage(msg);
      } catch (err) {
        console.error("[CommandRelay] Failed to parse message:", err);
      }
    };

    ws.onclose = (event) => {
      wsRef.current = null;
      setStatus("disconnected");

      if (event.code === 4001) {
        // Auth failed — don't reconnect, user needs to re-login
        setStatus("auth_error");
        console.error("[CommandRelay] Auth failed, not reconnecting");
        return;
      }

      console.log(
        `[CommandRelay] Connection closed, reconnecting in ${reconnectDelayRef.current}ms...`
      );
      
      reconnectTimeoutRef.current = setTimeout(() => {
        connect();
      }, reconnectDelayRef.current);
      
      reconnectDelayRef.current = Math.min(
        reconnectDelayRef.current * 2,
        MAX_RECONNECT_DELAY
      );
    };

    ws.onerror = (err) => {
      console.error("[CommandRelay] WebSocket error:", err);
      ws.close();
    };
  }, [token, handleMessage]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setStatus("disconnected");
  }, []);

  const sendCommand = useCallback((payload: SendCommandPayload): boolean => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error("[CommandRelay] Cannot send command: not connected");
      return false;
    }

    try {
      wsRef.current.send(JSON.stringify(payload));
      return true;
    } catch (err) {
      console.error("[CommandRelay] Failed to send command:", err);
      return false;
    }
  }, []);

  // Connect on mount if token exists, disconnect on unmount
  useEffect(() => {
    if (token) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [token, connect, disconnect]);

  return {
    status,
    onlineUsers,
    currentUser,
    sendCommand,
    connect,
    disconnect,
  };
}

// ============ Helper Functions ============
export function getCommandLabel(action: CommandAction, data: CommandData): string {
  switch (action) {
    case "TRACK_AWB":
      return `Track AWB ${data.awbNo || ""}`;
    case "OPEN_PICKUP":
      return `Track Pickup ${data.pickupId || ""}`;
    case "TEXT_MESSAGE":
      return data.message || "";
    default:
      return "Unknown command";
  }
}

export function getCommandNavigationUrl(
  action: CommandAction,
  data: CommandData
): string | null {
  switch (action) {
    case "TRACK_AWB":
      // AWB numbers starting with P and length >= 10 go to /pod/tracking
      // Otherwise it's treated as pickup tracking
      if (data.awbNo) {
        return `/pod/tracking?awb=${data.awbNo}`;
      }
      return null;
    case "OPEN_PICKUP":
      // Pickup AWB tracking (e.g., AHD12345)
      return data.pickupId ? `/pickup/tracking?awb=${data.pickupId}` : null;
    case "TEXT_MESSAGE":
      return null; // No navigation for text messages
    default:
      return null;
  }
}
